import React from 'react';
import { Calendar, AlertTriangle, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Deadline {
  id: string;
  title: string;
  type: 'proposal' | 'contract' | 'certification' | 'compliance';
  dueDate: string;
  agency?: string;
  value?: number;
  description: string;
  progress?: number;
  daysLeft: number;
}

const deadlines: Deadline[] = [
  {
    id: '1',
    title: 'IT Infrastructure Proposal',
    type: 'proposal',
    dueDate: '2024-03-14',
    agency: 'Department of Defense',
    value: 2500000,
    description: 'Technical proposal submission deadline',
    progress: 75,
    daysLeft: 5,
  },
  {
    id: '2',
    title: 'Cybersecurity Assessment',
    type: 'contract',
    dueDate: '2024-03-19',
    agency: 'Department of Homeland Security',
    value: 1800000,
    description: 'Contract review and signature deadline',
    progress: 90,
    daysLeft: 10,
  },
  {
    id: '3',
    title: 'ISO 27001 Certification',
    type: 'certification',
    dueDate: '2024-03-24',
    description: 'Annual certification renewal deadline',
    daysLeft: 15,
  },
];

export function DeadlineNotifications() {
  const navigate = useNavigate();

  const handleViewAll = () => {
    navigate('/deadlines', { state: { deadlines } });
  };

  const handleDeadlineClick = (deadline: Deadline) => {
    switch (deadline.type) {
      case 'proposal':
        navigate(`/proposals/${deadline.id}`);
        break;
      case 'contract':
        navigate(`/contracts/${deadline.id}`);
        break;
      case 'certification':
        navigate('/profile/certifications');
        break;
      case 'compliance':
        navigate('/compliance');
        break;
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900">Upcoming Deadlines</h2>
        <button
          onClick={handleViewAll}
          className="text-sm font-medium text-purple-600 hover:text-purple-500"
        >
          View all
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {deadlines.map((deadline) => (
          <button
            key={deadline.id}
            onClick={() => handleDeadlineClick(deadline)}
            className="w-full rounded-lg bg-red-50 p-4 text-left transition-colors hover:bg-red-100"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <div>
                  <h3 className="font-medium text-gray-900">{deadline.title}</h3>
                  <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    <span>Due {new Date(deadline.dueDate).toLocaleDateString()}</span>
                    {deadline.agency && (
                      <>
                        <span>•</span>
                        <span>{deadline.agency}</span>
                      </>
                    )}
                    {deadline.value && (
                      <>
                        <span>•</span>
                        <span>
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(deadline.value)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {deadline.progress !== undefined && (
                  <div className="flex items-center space-x-2">
                    <div className="h-2 w-20 rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-purple-600"
                        style={{ width: `${deadline.progress}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-500">{deadline.progress}%</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                    {deadline.daysLeft} days left
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
            <p className="mt-1 text-sm text-gray-500">{deadline.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}